/**
 * Streaming Sources Service
 * Uses FMHY recommended streaming sites for movies, TV, anime, and sports
 *
 * TMDb is only for metadata (posters, descriptions, etc.)
 * These sites provide actual video streams
 */

export type StreamQuality = '4K' | 'HD' | 'SD';
export type StreamPriority = 'primary' | 'backup' | 'fallback';

export interface StreamSource {
  id: string;
  name: string;
  url: string;
  type: 'embed' | 'redirect';
  quality: StreamQuality;
  hasAds: boolean;
  autoNext: boolean;
  status: StreamPriority;
}

// These providers are the only embeds allowed to run without the sandbox
// attribute they explicitly reject. CineVault still filters ad-supported
// sources out and keeps its own surf overlay between the viewer and the frame.
const SANDBOX_COMPATIBLE_SOURCE_IDS = new Set(['vidfast', 'vidlink', 'vidking']);

export function getSandboxCompatibleSources(sources: StreamSource[]): StreamSource[] {
  return sources.filter(
    source => !source.hasAds && SANDBOX_COMPATIBLE_SOURCE_IDS.has(source.id)
  );
}

// ============================================
// MOVIE & TV STREAMING SOURCES (from FMHY)
// ============================================

// Maximum coverage — many sources block server-side tests but work fine in browser iframes.
// Keep everything, let auto-switch find what works for the user.
// Order = display order (1-9). No-ads, most-reliable 2026 providers up top;
// ad-supported fallbacks at the bottom. Curated 2026-05-30 per Captain:
// pruned 2Embed/VidSrc.xyz/Smashy; current cable-safe set includes VidFast,
// Vidking, VidLink, CineSrc, and VidSrc.link.
export const EMBED_SOURCES = {
  // VidFast — modern no-ads provider, clean player, strong 2026 reliability. Primary.
  vidfast: {
    name: 'VidFast',
    movie: (tmdbId: number) => `https://vidfast.vc/movie/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://vidfast.vc/tv/${tmdbId}/${season}/${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },

  // Vidking — confirmed working
  vidking: {
    name: 'Vidking',
    movie: (tmdbId: number) => `https://www.vidking.net/embed/movie/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://www.vidking.net/embed/tv/${tmdbId}/${season}/${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },

  // VidLink — no ads (Captain: promote to #3)
  vidlink: {
    name: 'VidLink',
    movie: (tmdbId: number) => `https://vidlink.pro/movie/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://vidlink.pro/tv/${tmdbId}/${season}/${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },

  // NontonGo (Captain: move to #4)
  nontongo: {
    name: 'NontonGo',
    movie: (tmdbId: number) => `https://www.nontongo.win/embed/movie/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://www.nontongo.win/embed/tv/${tmdbId}/${season}/${episode}`,
    quality: 'HD' as const,
    hasAds: true,
  },

  // CineSrc
  cinesrc: {
    name: 'CineSrc',
    movie: (tmdbId: number) => `https://cinesrc.st/embed/movie/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://cinesrc.st/embed/tv/${tmdbId}?s=${season}&e=${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },

  // VidSrc.link — current documented vidsrc-family endpoint. Replaces the
  // retired vidsrc.vip hostname that no longer resolves.
  vidsrclink: {
    name: 'VidSrc.link',
    movie: (tmdbId: number) => `https://vidsrc.link/embed/movie/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://vidsrc.link/embed/tv/${tmdbId}/${season}/${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },

  // AutoEmbed — ad-supported fallback
  autoembed: {
    name: 'AutoEmbed',
    movie: (tmdbId: number) => `https://autoembed.co/movie/tmdb/${tmdbId}`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://autoembed.co/tv/tmdb/${tmdbId}-${season}-${episode}`,
    quality: 'HD' as const,
    hasAds: true,
  },

  // SuperEmbed / MultiEmbed — ad-supported fallback
  superembed: {
    name: 'SuperEmbed',
    movie: (tmdbId: number) => `https://multiembed.mov/?video_id=${tmdbId}&tmdb=1`,
    tv: (tmdbId: number, season: number, episode: number) =>
      `https://multiembed.mov/?video_id=${tmdbId}&tmdb=1&s=${season}&e=${episode}`,
    quality: 'HD' as const,
    hasAds: true,
  },
};

// Build source list from all embed sources
function buildMovieSources(tmdbId: number): StreamSource[] {
  const entries = Object.entries(EMBED_SOURCES);
  return entries.map(([id, source], i) => ({
    id,
    name: source.name + (source.hasAds ? '' : ' (No Ads)'),
    url: source.movie(tmdbId),
    type: 'embed' as const,
    quality: source.quality,
    hasAds: source.hasAds,
    autoNext: false,
    status: (i === 0 ? 'primary' : i < 3 ? 'backup' : 'fallback') as StreamPriority,
  }));
}

// Get all movie stream URLs for a TMDB ID
export function getMovieStreamUrls(tmdbId: number, _color?: string): StreamSource[] {
  return buildMovieSources(tmdbId);
}

// Get all TV stream URLs for a TMDB ID
export function getTvStreamUrls(
  tmdbId: number,
  season: number,
  episode: number,
  _color?: string
): StreamSource[] {
  const entries = Object.entries(EMBED_SOURCES);
  return entries.map(([id, source], i) => ({
    id,
    name: source.name + (source.hasAds ? '' : ' (No Ads)'),
    url: source.tv(tmdbId, season, episode),
    type: 'embed' as const,
    quality: source.quality,
    hasAds: source.hasAds,
    autoNext: false,
    status: (i === 0 ? 'primary' : i < 3 ? 'backup' : 'fallback') as StreamPriority,
  }));
}

// ============================================
// LIVE SPORTS STREAMING SOURCES (from FMHY Dec 2025)
// Full list: https://fmhy.net/video#live-sports
// ============================================

// ALL sports streaming sites from FMHY
export const SPORTS_STREAM_SITES = {
  // ⭐ STARRED (Best quality/reliability) - from FMHY
  starred: [
    { id: 'sportyhunter', name: 'SportyHunter', url: 'https://sportyhunter.net', type: 'aggregator', sports: ['all'] },
    { id: 'streamed', name: 'Streamed', url: 'https://streamed.su', type: 'aggregator', sports: ['all'] },
    { id: 'ntv', name: 'NTV', url: 'https://ntv.watch', type: 'aggregator', sports: ['all'] },
    { id: 'watchsports', name: 'WatchSports', url: 'https://watchsports.to', type: 'aggregator', sports: ['all'] },
    { id: 'daddylive', name: 'DaddyLive', url: 'https://daddylive.website', type: 'aggregator', sports: ['all'] },
    { id: 'ppvto', name: 'PPV.TO', url: 'https://ppv.to', type: 'live', sports: ['mma', 'boxing', 'ppv'] },
    { id: 'sport7', name: 'Sport7', url: 'https://sport7.live', type: 'aggregator', sports: ['all'] },
    { id: 'watchfooty', name: 'Watch Footy', url: 'https://watchfooty.live', type: 'aggregator', sports: ['soccer', 'all'] },
    { id: 'bintv', name: 'BINTV', url: 'https://bintv.live', type: 'aggregator', sports: ['all'] },
    { id: 'sportsbite', name: 'SportsBite', url: 'https://sportsbite.org', type: 'aggregator', sports: ['all'] },
    { id: 'streameast', name: 'StreamEast', url: 'https://streameast.to', type: 'aggregator', sports: ['all'] },
    { id: 'livetv', name: 'LiveTV', url: 'https://livetv.sx', type: 'aggregator', sports: ['all'] },
    { id: 'timstreams', name: 'TimStreams', url: 'https://timstreams.com', type: 'live', sports: ['all'] },
    { id: 'wearechecking', name: 'WeAreChecking', url: 'https://wearechecking.com', type: 'live', sports: ['motorsports', 'f1'] },
  ],

  // Regular sites
  regular: [
    { id: 'streamsports99', name: 'StreamSports99', url: 'https://streamsports99.com', type: 'aggregator', sports: ['all'] },
    { id: 'streamex', name: 'Streamex', url: 'https://streamex.live', type: 'aggregator', sports: ['all'] },
    { id: 'crackstreams', name: 'CrackStreams', url: 'https://crackstreams.io', type: 'aggregator', sports: ['all'] },
    { id: 'sportsurge', name: 'Sportsurge', url: 'https://sportsurge.io', type: 'aggregator', sports: ['all'] },
    { id: 'sportdb', name: 'SportDB', url: 'https://sportdb.live', type: 'aggregator', sports: ['all'] },
    { id: 'totalsportek', name: 'TotalSportek', url: 'https://totalsportek.es', type: 'aggregator', sports: ['all'] },
    { id: 'crichd', name: 'CricHD', url: 'https://crichd.com', type: 'aggregator', sports: ['cricket', 'all'] },
    { id: 'mainportal66', name: 'MainPortal66', url: 'https://mainportal66.com', type: 'aggregator', sports: ['all'] },
    { id: 'goalietrend', name: 'GoalieTrend', url: 'https://goalietrend.com', type: 'aggregator', sports: ['soccer'] },
    { id: 'streamcorner', name: 'StreamCorner', url: 'https://streamcorner.live', type: 'aggregator', sports: ['all'] },
    { id: 'viprow', name: 'VIPRow', url: 'https://viprow.nu', type: 'aggregator', sports: ['all'] },
    { id: 'nflbite', name: 'NFLBite', url: 'https://nflbite.com', type: 'aggregator', sports: ['nfl'] },
    { id: 'fctv33', name: 'FCTV33', url: 'https://fctv33.com', type: 'aggregator', sports: ['all'] },
    { id: 'mrgamingstreams', name: 'MrGamingStreams', url: 'https://mrgamingstreams.com', type: 'aggregator', sports: ['all'] },
    { id: 'sportontv', name: 'SportOnTV', url: 'https://sportontv.live', type: 'aggregator', sports: ['all'] },
    { id: 'sportsplus', name: 'Sports Plus', url: 'https://sportsplus.live', type: 'aggregator', sports: ['all'] },
    { id: 'streamfree', name: 'StreamFree', url: 'https://streamfree.live', type: 'aggregator', sports: ['all'] },
    { id: 'crackstreamsblog', name: 'CrackStreams.blog', url: 'https://crackstreams.blog', type: 'aggregator', sports: ['all'] },
    { id: 'vipboxsports', name: 'VIP Box Sports', url: 'https://vipboxsports.me', type: 'aggregator', sports: ['all'] },
    { id: '720pstream', name: '720pStream', url: 'https://720pstream.nu', type: 'aggregator', sports: ['all'] },
    { id: 'buffstream', name: 'BuffStream', url: 'https://buffstream.io', type: 'aggregator', sports: ['all'] },
    { id: 'sharkstreams', name: 'SharkStreams', url: 'https://sharkstreams.net', type: 'aggregator', sports: ['all'] },
    { id: 'crackstreamsms', name: 'CrackStreams.ms', url: 'https://crackstreams.ms', type: 'aggregator', sports: ['all'] },
  ],

  // Sport-specific sites
  specific: [
    { id: 'nbamonster', name: 'NBAMonster', url: 'https://nbamonster.net', type: 'aggregator', sports: ['nba'] },
    { id: 'nflbite', name: 'NFLBite', url: 'https://nflbite.com', type: 'aggregator', sports: ['nfl'] },
    { id: 'lsbc', name: 'LSBC', url: 'https://lsbc.live', type: 'aggregator', sports: ['soccer'] },
    { id: 'trendgola', name: 'Trendgola', url: 'https://trendgola.com', type: 'aggregator', sports: ['soccer'] },
    { id: 'starlive', name: 'StarLive', url: 'https://starlive.tv', type: 'live', sports: ['mma'] },
    { id: 'tflix', name: 'TFLIX', url: 'https://tflix.to', type: 'channels', sports: ['all'] },
    { id: 'webcric', name: 'WebCric', url: 'https://webcric.com', type: 'aggregator', sports: ['cricket'] },
    { id: 'onhockey', name: 'OnHockey', url: 'https://onhockey.tv', type: 'aggregator', sports: ['hockey'] },
    { id: 'overtakefans', name: 'OvertakeFans', url: 'https://overtakefans.com', type: 'aggregator', sports: ['f1', 'motorsports'] },
    { id: 'aceztrims', name: 'Aceztrims', url: 'https://aceztrims.com', type: 'aggregator', sports: ['motorsports'] },
    { id: 'dd12', name: 'DD12', url: 'https://dd12.live', type: 'aggregator', sports: ['motorsports'] },
    { id: 'nontongp', name: 'NontonGP', url: 'https://nontongp.com', type: 'aggregator', sports: ['motogp'] },
    { id: 'rugbystreams', name: 'RugbyStreams', url: 'https://reddit.com/r/rugbystreams', type: 'reddit', sports: ['rugby'] },
    { id: 'tizcycling', name: 'Tiz-Cycling', url: 'https://tiz-cycling.racing', type: 'aggregator', sports: ['cycling'] },
  ],
};

// NBA-specific stream URLs (Updated Dec 2025)
export const NBA_STREAM_URLS = {
  // Best sites for NBA - WORKING sources
  primary: [
    { id: 'sportplus', name: 'SportPlus (Best)', url: 'https://en12.sportplus.live/basketball/nba/', quality: 'HD' },
    { id: 'sportyhunter', name: 'SportyHunter', url: 'https://sportyhunter.com/sport/basketball', quality: 'HD' },
    { id: 'streamed', name: 'Streamed', url: 'https://streamed.su/category/basketball', quality: 'HD' },
    { id: 'streameast', name: 'StreamEast', url: 'https://streameast.host/sport/basketball', quality: 'HD' },
    { id: 'daddylive', name: 'DaddyLive', url: 'https://daddylive.mp', quality: 'HD' },
  ],
  // Backup sites
  backup: [
    { id: 'sportsurge', name: 'Sportsurge', url: 'https://v2.sportsurge.net/basketball', quality: 'HD' },
    { id: 'crackstreams', name: 'CrackStreams', url: 'https://crackstreams.io/nba-streams', quality: 'HD' },
    { id: 'buffstream', name: 'BuffStream', url: 'https://buffstream.io/nba-streams', quality: 'HD' },
    { id: 'methstreams', name: 'MethStreams', url: 'https://methstreams.com/nba-streams/', quality: 'HD' },
    { id: 'weakstreams', name: 'WeakStreams', url: 'https://weakstreams.com/nba-streams/', quality: 'HD' },
  ],
};

// Build game-specific stream URL (Updated Dec 2025)
function buildGameStreamUrl(site: string): string {
  const patterns: Record<string, string> = {
    // Primary - Best working sources
    sportplus: 'https://en12.sportplus.live/basketball/nba/',
    sportyhunter: 'https://sportyhunter.com/sport/basketball',
    streamed: 'https://streamed.su/category/basketball',
    streameast: 'https://streameast.host/sport/basketball',
    daddylive: 'https://daddylive.mp',
    // Backup sources
    sportsurge: 'https://v2.sportsurge.net/basketball',
    crackstreams: 'https://crackstreams.io/nba-streams',
    buffstream: 'https://buffstream.io/nba-streams',
    methstreams: 'https://methstreams.com/nba-streams/',
    weakstreams: 'https://weakstreams.com/nba-streams/',
  };

  return patterns[site] || patterns.sportplus;
}

// Get NBA game stream sources - EXTERNAL LINKS (opens in new tab)
// Updated Dec 2025 with working sources
export function getNBAStreamSources(): StreamSource[] {
  return [
    // Primary sources - Best quality, most reliable
    {
      id: 'sportplus',
      name: 'SportPlus (Best)',
      url: buildGameStreamUrl('sportplus'),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'primary',
    },
    {
      id: 'sportyhunter',
      name: 'SportyHunter',
      url: buildGameStreamUrl('sportyhunter'),
      type: 'redirect',
      quality: 'HD',
      hasAds: false,
      autoNext: false,
      status: 'primary',
    },
    {
      id: 'streamed',
      name: 'Streamed',
      url: buildGameStreamUrl('streamed'),
      type: 'redirect',
      quality: 'HD',
      hasAds: false,
      autoNext: false,
      status: 'primary',
    },
    {
      id: 'streameast',
      name: 'StreamEast',
      url: buildGameStreamUrl('streameast'),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'backup',
    },
    {
      id: 'daddylive',
      name: 'DaddyLive',
      url: buildGameStreamUrl('daddylive'),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'backup',
    },
    // Backup sources
    {
      id: 'sportsurge',
      name: 'Sportsurge',
      url: buildGameStreamUrl('sportsurge'),
      type: 'redirect',
      quality: 'HD',
      hasAds: false,
      autoNext: false,
      status: 'backup',
    },
    {
      id: 'methstreams',
      name: 'MethStreams',
      url: buildGameStreamUrl('methstreams'),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'fallback',
    },
    {
      id: 'weakstreams',
      name: 'WeakStreams',
      url: buildGameStreamUrl('weakstreams'),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'fallback',
    },
  ];
}

// Get all live sports streams for any sport
export function getSportsStreamSources(sport: 'nba' | 'nfl' | 'soccer' | 'mma' | 'f1' | 'all'): StreamSource[] {
  const sportUrls: Record<string, string> = {
    nba: '/nba',
    nfl: '/nfl',
    soccer: '/soccer',
    mma: '/mma',
    f1: '/f1',
    all: '',
  };

  const suffix = sportUrls[sport] || '';

  // Return starred sites first, then regular
  return [
    ...SPORTS_STREAM_SITES.starred.slice(0, 6).map((site, i) => ({
      id: site.id,
      name: site.name,
      url: site.url + suffix,
      type: 'redirect' as const,
      quality: 'HD' as const,
      hasAds: i > 2, // First 3 have no ads
      autoNext: false,
      status: 'primary' as const,
    })),
    ...SPORTS_STREAM_SITES.regular.slice(0, 4).map(site => ({
      id: site.id,
      name: site.name,
      url: site.url + suffix,
      type: 'redirect' as const,
      quality: 'HD' as const,
      hasAds: true,
      autoNext: false,
      status: 'backup' as const,
    })),
  ];
}

// ============================================
// ANIME STREAMING SOURCES (from FMHY)
// Now with EMBEDDABLE sources using TMDB IDs!
// ============================================

// Embeddable anime sources (use TMDB ID - plays IN-APP!)
export const ANIME_EMBED_SOURCES = {
  // VidSrc - Same as TV shows, works great for anime
  vidsrc: {
    name: 'VidSrc',
    anime: (tmdbId: number, season: number, episode: number) =>
      `https://vidsrc.xyz/embed/tv/${tmdbId}/${season}/${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },
  // Autoembed - Dedicated anime support
  autoembed: {
    name: 'AutoEmbed',
    anime: (tmdbId: number, season: number, episode: number) =>
      `https://autoembed.cc/anime/${tmdbId}-${season}-${episode}`,
    quality: 'HD' as const,
    hasAds: true,
  },
  // TMDB API - Another anime embed option
  tmdbapi: {
    name: 'TMDB API',
    anime: (tmdbId: number, season: number, episode: number) =>
      `https://www.tmdbapi.xyz/anime/${tmdbId}-${season}-${episode}`,
    quality: 'HD' as const,
    hasAds: true,
  },
  // Vidking - Our primary player also works for anime
  vidking: {
    name: 'Vidking',
    anime: (tmdbId: number, season: number, episode: number) =>
      `https://www.vidking.net/embed/tv/${tmdbId}/${season}/${episode}`,
    quality: '4K' as const,
    hasAds: false,
  },
  // Smashy - Good quality
  smashystream: {
    name: 'Smashy',
    anime: (tmdbId: number, season: number, episode: number) =>
      `https://player.smashy.stream/tv/${tmdbId}?s=${season}&e=${episode}`,
    quality: 'HD' as const,
    hasAds: false,
  },
};

// External anime sites (redirect - opens in new tab)
export const ANIME_EXTERNAL_SOURCES = {
  hianime: {
    name: 'HiAnime',
    search: (title: string) =>
      `https://hianime.to/search?keyword=${encodeURIComponent(title)}`,
    watch: (animeSlug: string, ep: number) =>
      `https://hianime.to/watch/${animeSlug}?ep=${ep}`,
  },
  nineanime: {
    name: '9Anime',
    search: (title: string) =>
      `https://9animetv.to/search?keyword=${encodeURIComponent(title)}`,
    watch: (animeSlug: string, ep: number) =>
      `https://9animetv.to/watch/${animeSlug}?ep=${ep}`,
  },
  aniwatch: {
    name: 'AniWatch',
    search: (title: string) =>
      `https://aniwatchtv.to/search?keyword=${encodeURIComponent(title)}`,
    watch: (animeSlug: string, ep: number) =>
      `https://aniwatchtv.to/watch/${animeSlug}?ep=${ep}`,
  },
};

// One Piece specific config
export const ONE_PIECE_CONFIG_STREAMING = {
  tmdbId: 37854,
  slug: 'one-piece-100', // For external sites
  totalSeasons: 22, // One Piece has many "seasons" on TMDB
};

// Get EMBEDDABLE anime stream sources (plays IN-APP!)
export function getAnimeEmbedUrls(
  tmdbId: number,
  season: number,
  episode: number,
  color?: string
): StreamSource[] {
  const colorParam = color
    ? `?color=${color.replace('#', '')}&autoPlay=true`
    : '?autoPlay=true';

  return [
    {
      id: 'vidking',
      name: 'Vidking (Best)',
      url: ANIME_EMBED_SOURCES.vidking.anime(tmdbId, season, episode) + colorParam,
      type: 'embed',
      quality: '4K',
      hasAds: false,
      autoNext: true,
      status: 'primary',
    },
    {
      id: 'vidsrc',
      name: 'VidSrc',
      url: ANIME_EMBED_SOURCES.vidsrc.anime(tmdbId, season, episode),
      type: 'embed',
      quality: 'HD',
      hasAds: false,
      autoNext: false,
      status: 'backup',
    },
    {
      id: 'smashystream',
      name: 'Smashy',
      url: ANIME_EMBED_SOURCES.smashystream.anime(tmdbId, season, episode),
      type: 'embed',
      quality: 'HD',
      hasAds: false,
      autoNext: false,
      status: 'backup',
    },
    {
      id: 'autoembed',
      name: 'AutoEmbed',
      url: ANIME_EMBED_SOURCES.autoembed.anime(tmdbId, season, episode),
      type: 'embed',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'fallback',
    },
  ];
}

// Get EXTERNAL anime sources (opens in new tab - HiAnime, 9Anime)
export function getAnimeExternalUrls(
  title: string,
  animeSlug?: string,
  episode?: number
): StreamSource[] {
  const ep = episode || 1;
  const slug = animeSlug || title.toLowerCase().replace(/\s+/g, '-');

  return [
    {
      id: 'hianime',
      name: 'HiAnime',
      url: animeSlug
        ? ANIME_EXTERNAL_SOURCES.hianime.watch(slug, ep)
        : ANIME_EXTERNAL_SOURCES.hianime.search(title),
      type: 'redirect',
      quality: 'HD',
      hasAds: false,
      autoNext: true,
      status: 'primary',
    },
    {
      id: 'nineanime',
      name: '9Anime',
      url: animeSlug
        ? ANIME_EXTERNAL_SOURCES.nineanime.watch(slug, ep)
        : ANIME_EXTERNAL_SOURCES.nineanime.search(title),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: true,
      status: 'backup',
    },
    {
      id: 'aniwatch',
      name: 'AniWatch',
      url: animeSlug
        ? ANIME_EXTERNAL_SOURCES.aniwatch.watch(slug, ep)
        : ANIME_EXTERNAL_SOURCES.aniwatch.search(title),
      type: 'redirect',
      quality: 'HD',
      hasAds: false,
      autoNext: true,
      status: 'backup',
    },
  ];
}

// Get ALL anime sources (embeds first, then external fallback)
export function getAnimeStreamSources(
  tmdbId: number,
  title: string,
  season: number,
  episode: number,
  animeSlug?: string
): StreamSource[] {
  // Embeddable sources first (in-app playback)
  const embedSources = getAnimeEmbedUrls(tmdbId, season, episode);

  // External fallback (HiAnime etc)
  const externalSources = getAnimeExternalUrls(title, animeSlug, episode);

  return [...embedSources, ...externalSources];
}

// ============================================
// CARTOON STREAMING SOURCES (from FMHY)
// For Cable TV mode - cartoons, kids shows
// ============================================

// External cartoon sites (opens in new tab) - from FMHY
export const CARTOON_EXTERNAL_SOURCES = {
  // WCO.TV - Best cartoon site (starred on FMHY)
  wco: {
    name: 'WCO.TV',
    base: 'https://www.wco.tv',
    search: (title: string) =>
      `https://www.wco.tv/search?keyword=${encodeURIComponent(title)}`,
  },
  // WCOStream - Alternative WCO domain
  wcostream: {
    name: 'WCOStream',
    base: 'https://www.wcostream.tv',
    search: (title: string) =>
      `https://www.wcostream.tv/search?keyword=${encodeURIComponent(title)}`,
  },
  // KimCartoon - Large cartoon library
  kimcartoon: {
    name: 'KimCartoon',
    base: 'https://kimcartoon.li',
    search: (title: string) =>
      `https://kimcartoon.li/Search/?s=${encodeURIComponent(title)}`,
  },
  // SuperCartoons - Classic cartoons
  supercartoons: {
    name: 'SuperCartoons',
    base: 'https://www.supercartoons.net',
    search: (title: string) =>
      `https://www.supercartoons.net/search/${encodeURIComponent(title.replace(/\s+/g, '+'))}`,
  },
  // ToonTales - Older/classic cartoons
  toontales: {
    name: 'ToonTales',
    base: 'https://toontales.net',
    search: (title: string) =>
      `https://toontales.net/?s=${encodeURIComponent(title)}`,
  },
  // B98.TV - Classic cartoons
  b98: {
    name: 'B98.TV',
    base: 'https://www.b98.tv',
    search: (title: string) =>
      `https://www.b98.tv/search?q=${encodeURIComponent(title)}`,
  },
};

// Get cartoon stream sources (external links for fallback)
export function getCartoonExternalUrls(title: string): StreamSource[] {
  return [
    {
      id: 'wco',
      name: 'WCO.TV (Best)',
      url: CARTOON_EXTERNAL_SOURCES.wco.search(title),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'primary',
    },
    {
      id: 'wcostream',
      name: 'WCOStream',
      url: CARTOON_EXTERNAL_SOURCES.wcostream.search(title),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'backup',
    },
    {
      id: 'kimcartoon',
      name: 'KimCartoon',
      url: CARTOON_EXTERNAL_SOURCES.kimcartoon.search(title),
      type: 'redirect',
      quality: 'HD',
      hasAds: true,
      autoNext: false,
      status: 'backup',
    },
    {
      id: 'supercartoons',
      name: 'SuperCartoons',
      url: CARTOON_EXTERNAL_SOURCES.supercartoons.search(title),
      type: 'redirect',
      quality: 'SD',
      hasAds: false,
      autoNext: false,
      status: 'fallback',
    },
  ];
}

// ============================================
// STREAM HEALTH & AUTO-SWITCH
// ============================================

interface StreamHealth {
  sourceId: string;
  lastChecked: number;
  isHealthy: boolean;
  errorCount: number;
  avgLoadTime: number;
}

// Store stream health in localStorage
const HEALTH_STORAGE_KEY = 'cinevault_stream_health';

export function getStreamHealth(): Record<string, StreamHealth> {
  try {
    const stored = localStorage.getItem(HEALTH_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function updateStreamHealth(
  sourceId: string,
  isHealthy: boolean,
  loadTime?: number
): void {
  const health = getStreamHealth();
  const current = health[sourceId] || {
    sourceId,
    lastChecked: 0,
    isHealthy: true,
    errorCount: 0,
    avgLoadTime: 0,
  };

  health[sourceId] = {
    ...current,
    lastChecked: Date.now(),
    isHealthy,
    errorCount: isHealthy ? 0 : current.errorCount + 1,
    avgLoadTime: loadTime
      ? (current.avgLoadTime + loadTime) / 2
      : current.avgLoadTime,
  };

  localStorage.setItem(HEALTH_STORAGE_KEY, JSON.stringify(health));
}

// Get best source based on health history
export function getBestSource(sources: StreamSource[]): StreamSource {
  const health = getStreamHealth();

  // Sort by: status priority, then health, then error count
  const sorted = [...sources].sort((a, b) => {
    const statusOrder = { primary: 0, backup: 1, fallback: 2 };
    const aStatus = statusOrder[a.status];
    const bStatus = statusOrder[b.status];

    if (aStatus !== bStatus) return aStatus - bStatus;

    const aHealth = health[a.id];
    const bHealth = health[b.id];

    if (aHealth && bHealth) {
      if (aHealth.isHealthy !== bHealth.isHealthy) {
        return aHealth.isHealthy ? -1 : 1;
      }
      return aHealth.errorCount - bHealth.errorCount;
    }

    return 0;
  });

  return sorted[0];
}

// Get next best source (excluding current)
export function getNextSource(
  sources: StreamSource[],
  currentSourceId: string
): StreamSource | null {
  const remaining = sources.filter((s) => s.id !== currentSourceId);
  if (remaining.length === 0) return null;

  // Mark current as unhealthy
  updateStreamHealth(currentSourceId, false);

  return getBestSource(remaining);
}
